import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChevronRight, RotateCcw, CheckCircle, Info } from 'lucide-react';
import { markAssessmentComplete } from '@/pages/ProgressPage';

// Assessment items with actions and questions
const ASSESSMENT_ITEMS = [
  {
    id: 'head-turn',
    name: 'Head Turn',
    instruction: 'Turn your head slowly to the left, then to the right. Notice any resistance.',
    question: 'Which side resists more?',
    options: ['Left', 'Right', 'Equal']
  },
  {
    id: 'shoulder-torso',
    name: 'Shoulder/Torso Turn',
    instruction: 'Turn your shoulders and torso slowly to the left, then to the right. Notice any resistance.',
    question: 'Which side resists more?',
    options: ['Left', 'Right', 'Equal']
  },
  {
    id: 'hip-turn',
    name: 'Hip Turn',
    instruction: 'Turn your hips slowly to the left, then to the right. Notice any resistance.',
    question: 'Which side resists more?',
    options: ['Left', 'Right', 'Equal']
  },
  {
    id: 'arm-lift',
    name: 'Arm Lift',
    instruction: 'Place your arms in your lap. Slowly lift both arms at the same time.',
    question: 'Which arm feels heavier?',
    options: ['Left', 'Right', 'Equal']
  },
  {
    id: 'arm-extension',
    name: 'Arm Extension',
    instruction: 'Slowly extend each arm independently, one at a time. Notice the smoothness of each movement.',
    question: 'Which arm is less smooth?',
    options: ['Left', 'Right', 'Equal']
  },
  {
    id: 'step-forward',
    name: 'Step Forward',
    instruction: 'Stand in a neutral position. Take one natural step forward.',
    question: 'Which foot did you lead with?',
    options: ['Left', 'Right']
  }
];

// Single leg balance assessment - 2 parts
const BALANCE_ASSESSMENT = {
  leftLeg: {
    id: 'balance-left',
    name: 'Left Leg Balance',
    instruction: 'Stand on your LEFT leg only. Keep your right foot off the ground. Try to maintain balance for 10 seconds.',
    duration: 10
  },
  rightLeg: {
    id: 'balance-right', 
    name: 'Right Leg Balance',
    instruction: 'Stand on your RIGHT leg only. Keep your left foot off the ground. Try to maintain balance for 10 seconds.',
    duration: 10
  },
  question: 'Which leg felt LESS balanced?',
  options: ['Left', 'Right', 'Equal']
};

const ASSESSMENT_DURATION = 10; // 10 seconds per assessment

// Tooltip component for flyover explanations
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div className="relative inline-block">
      <div 
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs font-body bg-obsidian-night text-ivory-mist rounded-lg shadow-lg w-64 text-center">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-obsidian-night"></div>
        </div>
      )}
    </div>
  );
};

const UserAssessment = ({ onComplete, existingAssessment = null }) => {
  const [phase, setPhase] = useState('intro'); // intro, handedness, assessment, question, balance-left, balance-right, balance-question, analysis, complete
  const [handedness, setHandedness] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [assessmentMs, setAssessmentMs] = useState(0); // Use milliseconds for smooth progress
  const [answers, setAnswers] = useState({});
  const [analysisResult, setAnalysisResult] = useState(null);
  const [balanceMs, setBalanceMs] = useState(0); // Timer for balance assessment

  // Timer for assessment phase - updates every 50ms for smooth progress
  useEffect(() => {
    let interval;
    if (phase === 'assessment') {
      interval = setInterval(() => {
        setAssessmentMs(prev => {
          const newMs = prev + 50;
          if (newMs >= ASSESSMENT_DURATION * 1000) {
            setPhase('question');
            return 0;
          }
          return newMs;
        });
      }, 50);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [phase]);

  // Timer for balance assessment
  useEffect(() => {
    let interval;
    if (phase === 'balance-left' || phase === 'balance-right') {
      interval = setInterval(() => {
        setBalanceMs(prev => {
          const newMs = prev + 50;
          if (newMs >= BALANCE_ASSESSMENT.leftLeg.duration * 1000) {
            if (phase === 'balance-left') {
              setPhase('balance-right');
            } else {
              setPhase('balance-question');
            }
            return 0;
          }
          return newMs;
        });
      }, 50);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [phase]);

  // Calculate seconds remaining and progress
  const assessmentSeconds = Math.floor(assessmentMs / 1000);
  const progress = (assessmentMs / (ASSESSMENT_DURATION * 1000)) * 100;
  const balanceSeconds = Math.floor(balanceMs / 1000);
  const balanceProgress = (balanceMs / (BALANCE_ASSESSMENT.leftLeg.duration * 1000)) * 100;

  const startAssessment = useCallback(() => {
    setPhase('handedness');
  }, []);

  const selectHandedness = useCallback((hand) => {
    setHandedness(hand);
    setPhase('assessment');
    setCurrentItemIndex(0);
    setAssessmentMs(0);
  }, []);

  const analyzeResults = useCallback((finalAnswers) => {
    // Analyze the assessment results to determine twist pattern
    let leftCount = 0;
    let rightCount = 0;

    Object.entries(finalAnswers).forEach(([key, value]) => {
      if (key !== 'balance' && value === 'Left') leftCount++;
      else if (key !== 'balance' && value === 'Right') rightCount++;
    });

    let twistDirection = 'neutral';
    let dominantSide = handedness;
    let oppositeLeg = handedness === 'Right' ? 'Left' : 'Right';
    let oppositeSideLunge = handedness === 'Right' ? 'Left' : 'Right';
    
    // Determine lead arm based on arm assessments
    const heavierArm = finalAnswers['arm-lift'] || 'Equal';
    const lessSmoothArm = finalAnswers['arm-extension'] || 'Equal';
    
    // Determine lead arm - prioritize if both assessments agree
    let leadArm = handedness === 'Right' ? 'Left' : 'Right'; // Default to opposite of dominant
    if (heavierArm !== 'Equal' && lessSmoothArm !== 'Equal') {
      if (heavierArm === lessSmoothArm) {
        leadArm = heavierArm;
      } else {
        leadArm = heavierArm;
      }
    } else if (heavierArm !== 'Equal') {
      leadArm = heavierArm;
    } else if (lessSmoothArm !== 'Equal') {
      leadArm = lessSmoothArm;
    }

    if (leftCount > rightCount) {
      twistDirection = 'left';
    } else if (rightCount > leftCount) {
      twistDirection = 'right';
    }

    // Determine weaker balance leg
    const balanceAnswer = finalAnswers['balance'] || 'Equal';
    let weakerBalanceLeg = balanceAnswer; // 'Left', 'Right', or 'Equal'
    
    const result = {
      handedness,
      answers: finalAnswers,
      twistDirection,
      dominantSide,
      oppositeLeg,
      oppositeSideLunge,
      leadArm,
      heavierArm,
      lessSmoothArm,
      weakerBalanceLeg, // The leg that was less balanced - will be challenged with single leg stance
      leftResistanceCount: leftCount,
      rightResistanceCount: rightCount,
      timestamp: new Date().toISOString()
    };

    setAnalysisResult(result);
    setPhase('analysis');
  }, [handedness]);

  const answerQuestion = useCallback((answer) => {
    const currentItem = ASSESSMENT_ITEMS[currentItemIndex];
    const newAnswers = {
      ...answers,
      [currentItem.id]: answer
    };
    setAnswers(newAnswers);

    if (currentItemIndex < ASSESSMENT_ITEMS.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
      setAssessmentMs(0);
      setPhase('assessment');
    } else {
      // All movement assessments complete - start balance assessment
      setPhase('balance-left');
      setBalanceMs(0);
    }
  }, [currentItemIndex, answers]);

  const answerBalanceQuestion = useCallback((answer) => {
    const finalAnswers = {
      ...answers,
      balance: answer
    };
    analyzeResults(finalAnswers);
  }, [answers, analyzeResults]);

  const completeAssessment = useCallback(() => {
    setPhase('complete');
    markAssessmentComplete();
    if (onComplete) {
      onComplete(analysisResult);
    }
  }, [analysisResult, onComplete]);

  const resetAssessment = useCallback(() => {
    setPhase('intro');
    setHandedness(null);
    setCurrentItemIndex(0);
    setAssessmentMs(0);
    setBalanceMs(0);
    setAnswers({});
    setAnalysisResult(null);
  }, []);

  const currentItem = ASSESSMENT_ITEMS[currentItemIndex];

  // Intro screen
  if (phase === 'intro') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <h2 className="font-display text-2xl font-bold text-obsidian-night text-center">
          Physical Pattern Assessment
        </h2>
        
        <div className="max-w-md text-center space-y-4">
          <p className="text-shadow-pine/80 font-body">
            This assessment will help us understand your natural movement patterns and identify where you might be fighting against yourself.
          </p>
          
          <p className="text-shadow-pine/60 font-body text-sm">
            We will use the coordinates from this assessment to challenge your patterns and oppose them during your calibration sessions.
          </p>
        </div>

        <div className="bg-burnt-terra/10 rounded-lg p-4 max-w-sm">
          <h3 className="font-body font-semibold text-burnt-terra mb-2">What to expect:</h3>
          <ul className="text-sm text-shadow-pine/70 font-body space-y-1">
            <li>• 6 movement assessments</li>
            <li>• 1 balance assessment (10 sec each leg)</li>
            <li>• Answer questions about what you notice</li>
            <li>• Receive personalized insights</li>
          </ul>
        </div>

        <Button
          onClick={startAssessment}
          className="bg-burnt-terra hover:bg-burnt-terra/90 text-white px-8 py-3"
        >
          Begin Assessment
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  }

  // Handedness selection
  if (phase === 'handedness') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <h2 className="font-display text-2xl font-bold text-obsidian-night text-center">
          First Question
        </h2>
        
        <p className="text-shadow-pine/80 font-body text-center max-w-md">
          Are you left-handed or right-handed?
        </p>

        <div className="flex gap-4">
          <Button
            onClick={() => selectHandedness('Left')}
            variant="outline"
            className="px-8 py-6 text-lg border-2 border-burnt-terra/30 hover:bg-burnt-terra/10 hover:border-burnt-terra"
          >
            Left-Handed
          </Button>
          <Button
            onClick={() => selectHandedness('Right')}
            variant="outline"
            className="px-8 py-6 text-lg border-2 border-burnt-terra/30 hover:bg-burnt-terra/10 hover:border-burnt-terra"
          >
            Right-Handed
          </Button>
        </div>
      </div>
    );
  }

  // Assessment phase (10-second timer)
  if (phase === 'assessment') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <div className="text-sm text-shadow-pine/60 font-body">
          Assessment {currentItemIndex + 1} of {ASSESSMENT_ITEMS.length}
        </div>
        
        <h2 className="font-display text-2xl font-bold text-obsidian-night text-center">
          {currentItem.name}
        </h2>

        <div className="max-w-md text-center">
          <p className="text-shadow-pine/80 font-body text-lg">
            {currentItem.instruction}
          </p>
        </div>

        <div className="w-full max-w-md space-y-2">
          <div className="flex justify-between text-sm text-shadow-pine/60 font-body">
            <span>Time remaining</span>
            <span>{ASSESSMENT_DURATION - assessmentSeconds}s</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>

        <div className="text-6xl font-display font-bold text-burnt-terra">
          {ASSESSMENT_DURATION - assessmentSeconds}
        </div>

        <p className="text-shadow-pine/60 font-body text-sm italic">
          Take your time to notice the sensations...
        </p>
      </div>
    );
  }

  // Question phase
  if (phase === 'question') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <div className="text-sm text-shadow-pine/60 font-body">
          Assessment {currentItemIndex + 1} of {ASSESSMENT_ITEMS.length}
        </div>
        
        <h2 className="font-display text-xl font-bold text-obsidian-night text-center">
          {currentItem.name}
        </h2>

        <p className="text-shadow-pine/80 font-body text-lg text-center max-w-md">
          {currentItem.question}
        </p>

        <div className="flex flex-wrap gap-3 justify-center">
          {currentItem.options.map((option) => (
            <Button
              key={option}
              onClick={() => answerQuestion(option)}
              variant="outline"
              className="px-6 py-4 text-lg border-2 border-burnt-terra/30 hover:bg-burnt-terra/10 hover:border-burnt-terra"
            >
              {option}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  // Balance assessment - Left leg
  if (phase === 'balance-left') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <div className="text-sm text-shadow-pine/60 font-body">
          Balance Assessment - Part 1 of 2
        </div>
        
        <h2 className="font-display text-2xl font-bold text-obsidian-night text-center">
          {BALANCE_ASSESSMENT.leftLeg.name}
        </h2>

        <div className="max-w-md text-center">
          <p className="text-shadow-pine/80 font-body text-lg">
            {BALANCE_ASSESSMENT.leftLeg.instruction}
          </p>
        </div>

        <div className="w-full max-w-md space-y-2">
          <div className="flex justify-between text-sm text-shadow-pine/60 font-body">
            <span>Time remaining</span>
            <span>{BALANCE_ASSESSMENT.leftLeg.duration - balanceSeconds}s</span>
          </div>
          <Progress value={balanceProgress} className="h-3" />
        </div>

        <div className="text-6xl font-display font-bold text-burnt-terra">
          {BALANCE_ASSESSMENT.leftLeg.duration - balanceSeconds}
        </div>

        <p className="text-shadow-pine/60 font-body text-sm italic">
          Notice any wobbling or need to touch down...
        </p>
      </div>
    );
  }

  // Balance assessment - Right leg
  if (phase === 'balance-right') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <div className="text-sm text-shadow-pine/60 font-body">
          Balance Assessment - Part 2 of 2
        </div>
        
        <h2 className="font-display text-2xl font-bold text-obsidian-night text-center">
          {BALANCE_ASSESSMENT.rightLeg.name}
        </h2>

        <div className="max-w-md text-center">
          <p className="text-shadow-pine/80 font-body text-lg">
            {BALANCE_ASSESSMENT.rightLeg.instruction}
          </p>
        </div>

        <div className="w-full max-w-md space-y-2">
          <div className="flex justify-between text-sm text-shadow-pine/60 font-body">
            <span>Time remaining</span>
            <span>{BALANCE_ASSESSMENT.rightLeg.duration - balanceSeconds}s</span>
          </div>
          <Progress value={balanceProgress} className="h-3" />
        </div>

        <div className="text-6xl font-display font-bold text-burnt-terra">
          {BALANCE_ASSESSMENT.rightLeg.duration - balanceSeconds}
        </div>

        <p className="text-shadow-pine/60 font-body text-sm italic">
          Notice any wobbling or need to touch down...
        </p>
      </div>
    );
  }

  // Balance question
  if (phase === 'balance-question') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <div className="text-sm text-shadow-pine/60 font-body">
          Balance Assessment
        </div>
        
        <h2 className="font-display text-xl font-bold text-obsidian-night text-center">
          Single Leg Balance
        </h2>

        <p className="text-shadow-pine/80 font-body text-lg text-center max-w-md">
          {BALANCE_ASSESSMENT.question}
        </p>

        <div className="flex flex-wrap gap-3 justify-center">
          {BALANCE_ASSESSMENT.options.map((option) => (
            <Button
              key={option}
              onClick={() => answerBalanceQuestion(option)}
              variant="outline"
              className="px-6 py-4 text-lg border-2 border-burnt-terra/30 hover:bg-burnt-terra/10 hover:border-burnt-terra"
            >
              {option}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  // Analysis phase with tooltips
  if (phase === 'analysis') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <CheckCircle className="w-16 h-16 text-shadow-pine" />
        
        <h2 className="font-display text-2xl font-bold text-obsidian-night text-center">
          Assessment Complete
        </h2>

        <div className="max-w-md space-y-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-burnt-terra/10">
            <h3 className="font-body font-semibold text-burnt-terra mb-3">Your Pattern Profile:</h3>
            
            <div className="space-y-3 text-sm font-body">
              <div className="flex items-center justify-between">
                <span className="text-shadow-pine/60">Dominant Hand:</span>
                <span className="font-semibold text-shadow-pine">{analysisResult?.handedness}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <Tooltip content="Your body's natural rotational tendency. We'll focus exercises in the opposite direction to create balance and restore neutral alignment.">
                  <span className="text-shadow-pine/60 flex items-center gap-1">
                    Twist Direction
                    <Info className="w-3.5 h-3.5 text-burnt-terra" />
                  </span>
                </Tooltip>
                <span className="font-semibold text-shadow-pine capitalize">{analysisResult?.twistDirection}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <Tooltip content="The arm that showed more resistance or felt heavier. This arm will lead exercises to strengthen and improve its coordination.">
                  <span className="text-shadow-pine/60 flex items-center gap-1">
                    Lead Arm
                    <Info className="w-3.5 h-3.5 text-burnt-terra" />
                  </span>
                </Tooltip>
                <span className="font-semibold text-shadow-pine">{analysisResult?.leadArm}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <Tooltip content="The leg opposite to your dominant side. During breaks, you'll position this leg forward to challenge your habitual stance patterns.">
                  <span className="text-shadow-pine/60 flex items-center gap-1">
                    Opposite Leg
                    <Info className="w-3.5 h-3.5 text-burnt-terra" />
                  </span>
                </Tooltip>
                <span className="font-semibold text-shadow-pine">{analysisResult?.oppositeLeg}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <Tooltip content="The leg that showed less stability during single-leg stance. Single leg exercises will challenge this leg to improve balance.">
                  <span className="text-shadow-pine/60 flex items-center gap-1">
                    Balance Challenge Leg
                    <Info className="w-3.5 h-3.5 text-burnt-terra" />
                  </span>
                </Tooltip>
                <span className="font-semibold text-shadow-pine">{analysisResult?.weakerBalanceLeg}</span>
              </div>
            </div>
          </div>

          <div className="bg-burnt-terra/10 rounded-lg p-4">
            <h3 className="font-body font-semibold text-burnt-terra mb-2">During your sessions, we will focus on:</h3>
            <ul className="text-sm text-shadow-pine/80 font-body space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-burnt-terra">•</span>
                <span><strong>Twist Direction ({analysisResult?.twistDirection}):</strong> Exercises will counter-rotate to restore balance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-burnt-terra">•</span>
                <span><strong>Lead Arm ({analysisResult?.leadArm}):</strong> This arm initiates movements to build coordination</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-burnt-terra">•</span>
                <span><strong>Opposite Leg ({analysisResult?.oppositeLeg}):</strong> Forward lunges challenge your stance patterns</span>
              </li>
              {analysisResult?.weakerBalanceLeg !== 'Equal' && (
                <li className="flex items-start gap-2">
                  <span className="text-burnt-terra">•</span>
                  <span><strong>Single Leg ({analysisResult?.weakerBalanceLeg}):</strong> Balance challenges on your weaker leg</span>
                </li>
              )}
            </ul>
          </div>

          <p className="text-shadow-pine/60 font-body text-sm text-center italic">
            These coordinates will be used to challenge your patterns and create lasting change.
          </p>
        </div>

        <div className="flex gap-4">
          <Button
            onClick={resetAssessment}
            variant="outline"
            className="border-burnt-terra/30 hover:bg-burnt-terra/10"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Retake Assessment
          </Button>
          <Button
            onClick={completeAssessment}
            className="bg-burnt-terra hover:bg-burnt-terra/90 text-white"
          >
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Complete phase
  if (phase === 'complete') {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-ivory-mist to-white rounded-lg">
        <CheckCircle className="w-16 h-16 text-shadow-pine" />
        <h2 className="font-display text-2xl font-bold text-obsidian-night text-center">
          Assessment Saved
        </h2>
        <p className="text-shadow-pine/70 font-body text-center">
          Your assessment results have been saved and will be used to customize your calibration sessions.
        </p>
      </div>
    );
  }

  return null;
};

export default UserAssessment;
